import {
  Autocomplete,
  AutocompleteProps,
  FormControl,
  TextField,
  Typography
} from '@mui/material'
import { SyntheticEvent, useEffect, useState } from 'react'
import {
  CommonFieldProps,
  CommonInputProps,
  FieldWrapper,
  FieldWrapperProps,
  LoadingWrapper,
  SelectOption
} from '.'
import {
  LocalizedOptionsKeyType,
  useFieldLabelAndPlaceholder,
  useFieldProps,
  useLocalizedOptionsValue
} from '../hooks'
import { getComponentId } from '../utils'

interface ComboboxProps
  extends Pick<
      AutocompleteProps<SelectOption, undefined, undefined, undefined>,
      'disabled' | 'options'
    >,
    CommonInputProps,
    CommonFieldProps {}

export const Combobox = ({
  executeOnChangeFn,
  disabled,
  fontSize,
  label,
  labelTranslationId,
  name,
  options,
  placeholder,
  placeholderTranslationId,
  required,
  variant,
  ...textFieldProps
}: ComboboxProps) => {
  const { error, helperText, onBlur, setValue, value } = useFieldProps(name)

  const [selectedOption, setSelectedOption] = useState<SelectOption | null>(
    null
  )

  const { fieldLabel, fieldPlaceholder } = useFieldLabelAndPlaceholder(
    labelTranslationId,
    label,
    placeholderTranslationId,
    placeholder
  )

  useEffect(() => {
    const optionValue = options?.find(
      (element: SelectOption) => element.value === value
    )
    if (optionValue) {
      setSelectedOption(optionValue)
    } else {
      setSelectedOption(null)
    }
  }, [options, value])

  const isLoading = !options || options.length === 0 || options[0] === null

  const handleChange = (
    _event: SyntheticEvent<Element, Event>,
    selected: SelectOption | null
  ) => {
    setSelectedOption(selected)
    const selectedValue = selected?.value
    setValue(selectedValue)
    if (executeOnChangeFn) {
      executeOnChangeFn(selectedValue)
    }
  }

  return (
    <FormControl>
      <LoadingWrapper
        isLoading={isLoading}
        spinnerProps={{ size: '2rem', sx: { mt: 2 } }}
      >
        {options != null && (
          <Autocomplete
            blurOnSelect
            getOptionLabel={(option: SelectOption) => option.display || ''}
            id={getComponentId('combo-box')}
            renderOption={(props, option: SelectOption) => (
              <li {...props}>
                <Typography style={{ fontSize }}>{option.display}</Typography>
              </li>
            )}
            disabled={disabled}
            onBlur={onBlur}
            onChange={handleChange}
            options={options}
            renderInput={(params) => (
              <TextField
                {...params}
                {...textFieldProps}
                error={!!error}
                InputProps={{
                  ...params.InputProps,
                  sx: { fontSize }
                }}
                helperText={helperText}
                label={fieldLabel}
                name={name}
                placeholder={fieldPlaceholder}
                required={required || false}
                variant={variant || 'standard'}
              />
            )}
            value={selectedOption}
          />
        )}
      </LoadingWrapper>
    </FormControl>
  )
}

export type FormikComboboxProps = Partial<FieldWrapperProps> & ComboboxProps

export const FormikCombobox = ({
  hidefield = false,
  gridProps,
  ...rest
}: FormikComboboxProps) => {
  return (
    <FieldWrapper hidefield={hidefield} gridProps={{ md: gridProps?.md || 4 }}>
      <Combobox {...rest} />
    </FieldWrapper>
  )
}

interface LocalizedComboboxProps extends Omit<FormikComboboxProps, 'options'> {
  localizedOptionsKey: LocalizedOptionsKeyType
}

export const LocalizedCombobox = ({
  localizedOptionsKey,
  ...props
}: LocalizedComboboxProps) => {
  const options = useLocalizedOptionsValue(localizedOptionsKey)

  return <FormikCombobox {...props} options={options} />
}
